/**
 * Tiny display-only highlighter for knitting patterns, in the manner of a printed pattern: row
 * labels as soft serif headings, section titles a size up, multi-size groups gently highlighted,
 * the front matter in quiet italics. The engine alone decides what the text means.
 */
import { HighlightStyle, StreamLanguage, syntaxHighlighting } from '@codemirror/language';
import { Tag, tags as t } from '@lezer/highlight';
import type { Extension } from '@codemirror/state';

const sizeGroupTag = Tag.define();
const repeatTag = Tag.define();

const FRONT_MATTER = /^#\s*[A-Za-z_][\w-]*\s*:.*$/;
const ROW_LABEL =
  /^\s*(?:rows?|rnds?|rounds?|rangs?|rgs?|tours?|r)\s*\.?\s*\d+(?:\s*(?:-|–|à|to|and|et|&)\s*\d+)?(?:\s*\([^)]{0,16}\))?\s*:/i;
/** A short capitalised line without figures or final punctuation: « Calotte », "Turn the heel". */
const HEADING = /^\s*[A-ZÀ-ÖØ-Þ][^\d:;.,!?*()]{1,38}\s*$/;
const SIZE_GROUP =
  /^(?:[A-Za-z]*\d+(?:[.,]\d+)?[½¼¾]?\s*\(\s*(?:\d+(?:[.,]\d+)?[½¼¾]?|[-–—])(?:\s*[,;]\s*(?:\d+(?:[.,]\d+)?[½¼¾]?|[-–—]))*\s*\)|\d+(?:-\d+){2,}\b|\d+(?:\s:\s\d+){2,})/;
const NUMBER = /^\d+(?:[.,]\d+)?/;

interface PatternState {
  inHeader: boolean;
}

const patternStream = StreamLanguage.define<PatternState>({
  name: 'knitting-pattern',
  startState: () => ({ inHeader: true }),
  copyState: (s) => ({ ...s }),
  token(stream, state) {
    if (stream.sol()) {
      if (state.inHeader && stream.match(FRONT_MATTER)) return 'frontMatter';
      if (!stream.match(/^\s*$/, false)) state.inHeader = false;
      if (stream.match(ROW_LABEL)) return 'rowLabel';
      if (stream.match(HEADING)) return 'heading';
    }
    if (stream.eatSpace()) return null;
    if (stream.match(SIZE_GROUP)) return 'sizeGroup';
    if (stream.match(NUMBER)) return 'number';
    if (stream.eat('*')) return 'repeat';
    stream.next();
    stream.eatWhile(/[^\s\d*]/);
    return null;
  },
  tokenTable: {
    frontMatter: t.meta,
    rowLabel: t.labelName,
    heading: t.heading,
    sizeGroup: sizeGroupTag,
    number: t.number,
    repeat: repeatTag,
  },
});

const patternHighlight = HighlightStyle.define([
  { tag: t.meta, color: 'rgb(var(--c-fg-subtle))', fontStyle: 'italic', fontSize: '13px' },
  {
    tag: t.labelName,
    color: 'rgb(var(--c-label))',
    fontFamily: 'var(--font-serif)',
    fontWeight: '560',
    fontVariationSettings: "'SOFT' 40, 'WONK' 0",
  },
  {
    tag: t.heading,
    color: 'rgb(var(--c-fg))',
    fontFamily: 'var(--font-serif)',
    fontSize: '18px',
    fontWeight: '500',
    fontVariationSettings: "'SOFT' 40, 'WONK' 0",
  },
  { tag: t.number, fontWeight: '600' },
  {
    tag: sizeGroupTag,
    fontWeight: '600',
    color: 'rgb(var(--c-sizes))',
    backgroundColor: 'rgb(var(--c-sizes) / 0.09)',
    borderRadius: '3px',
    padding: '0 2px',
  },
  { tag: repeatTag, color: 'rgb(var(--c-repeat))', fontWeight: '700' },
]);

export function patternLanguage(): Extension {
  return [patternStream, syntaxHighlighting(patternHighlight)];
}
